/**
 * AUTH-HANDOFF — carrying a session from the login origin to a persona SPA on a DIFFERENT site.
 *
 * ─── the problem ──────────────────────────────────────────────────────────────────────────────────
 * Tokens are memory-only (`sessionStore`), so a cross-origin redirect always loses them; the
 * destination SPA restores the session from the httpOnly refresh cookie instead. That works as long
 * as the login origin and the SPA are the SAME SITE — the cookie is `Domain=<apex>; SameSite=Strict`
 * and spans the persona subdomains.
 *
 * It stops working the moment the login app is served from a different registrable domain (a tenant's
 * own branded domain). The login call is then a cross-site request, so the browser treats the
 * `Set-Cookie` as a third-party write and blocks or partitions it. The SPA boots, finds no cookie,
 * goes `guest`, and `ProtectedRoute` bounces the user straight back to the login app — a loop that
 * looks exactly like a routing bug and is not one. CORS does not help: it governs whether the request
 * is allowed, not whether the browser keeps the cookie.
 *
 * ─── the fix ──────────────────────────────────────────────────────────────────────────────────────
 * Hand the token pair to the destination in the URL FRAGMENT. The SPA seeds them into its store, and
 * the `authApi.refresh()` its bootstrap already runs sends the refresh token IN THE BODY (see
 * `doRefresh` — the cookie is only its fallback). That request is same-site with Identity, so the
 * cookie Identity sets in response is FIRST-PARTY and every later load restores normally. The
 * handoff is needed exactly once, on the hop that crosses sites.
 *
 * ─── why the fragment, and what it costs ──────────────────────────────────────────────────────────
 * A fragment is never sent to a server and never appears in `Referer`, which a query string is and
 * does. It is still in the address bar and in history until stripped, so `consumeAuthHandoff` strips
 * it with `replaceState` before anything else and the backend rotates the token on first use with
 * reuse detection — the exposure is one hop.
 *
 * ⚠ IT IS STILL A REFRESH TOKEN IN A URL. The cleaner shape is a short-lived single-use handoff CODE
 * minted by Identity and exchanged by the SPA, which needs a backend endpoint pair; this is the
 * frontend-only form of the same idea. Do not widen it: it is emitted ONLY on a genuine cross-site
 * hop (`needsHandoff`), ONLY to a URL the registry already validated, and ONLY over a secure context.
 *
 * ⚠ IT DEPENDS ON THE LOGIN RESPONSE STILL CARRYING `refreshToken` IN THE BODY. `loginResponseSchema`
 * marks it optional, so a backend that stops sending it would silently disable the handoff — the loop
 * would come back, with nothing in the console to say why. `hasHandoffTokens` is the check; if this
 * ever goes quiet, look there first.
 */
import { useSessionStore } from './sessionStore';

/** Fragment keys, prefixed so they cannot collide with an app's own fragment routing. */
const ACCESS_KEY = 'zc_at';
const REFRESH_KEY = 'zc_rt';

export interface HandoffTokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * Minimal `window.location` shape this module needs (injectable for tests).
 *
 * ⚠ `pathname`/`search` ARE PART OF IT so the stripped URL is rebuilt from the INJECTED location
 * rather than read back off `window` — a test that stubs only the hash would otherwise reach for the
 * real `window`, and because the strip is wrapped in a try/catch the failure would be swallowed and
 * the test would pass while the fragment stayed in the address bar. That happened while writing this.
 */
export interface HandoffLocation {
  protocol: string; // includes the trailing ':'
  hostname: string;
  hash: string;
  pathname: string;
  search: string;
}

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

/**
 * A token may only travel through a URL over a secure context. `localhost` counts as secure to the
 * browser and is where dev runs, so it is allowed; anything else on plain http is refused outright
 * rather than downgraded.
 */
export function isSecureContextHost(protocol: string, hostname: string): boolean {
  return protocol === 'https:' || LOCAL_HOSTS.has(hostname);
}

/**
 * Would the refresh cookie already be shared between these two hosts? When yes, NO handoff is
 * emitted — the existing cookie mechanism works and putting a token in the URL would be exposure
 * bought for nothing. This is the whole guard, so it is deliberately conservative: it answers "yes,
 * same site" only in cases that are unambiguous, and anything else falls through to a handoff.
 *
 * ⚠ IT CANNOT COMPUTE THE REGISTRABLE DOMAIN, and does not try. That needs the public suffix list
 * (`teeneagle.co.uk` is an apex, `co.uk` is not), which is not worth shipping to every SPA. The three
 * cases below cover every topology this product actually deploys:
 *   - the same host                              `admin.x` → `admin.x`   (and localhost:3001 → :3003,
 *                                                 since cookies ignore the port)
 *   - apex ↔ subdomain                           `teeneagle.co.uk` → `admin.teeneagle.co.uk`
 *   - two subdomains of one parent               `login.demo.x` → `teacher.demo.x`
 * A wrong answer in the safe direction (a needless handoff) costs one extra fragment; a wrong answer
 * the other way costs a login loop. So unknown means handoff.
 */
export function needsHandoff(currentHost: string, targetHost: string): boolean {
  if (currentHost === targetHost) return false;
  if (targetHost.endsWith(`.${currentHost}`)) return false;
  if (currentHost.endsWith(`.${targetHost}`)) return false;

  // two subdomains of a shared parent — only when BOTH still have a parent left to compare, so
  // `a.com` vs `b.com` does not "match" on the bare TLD.
  const currentParent = currentHost.split('.').slice(1).join('.');
  const targetParent = targetHost.split('.').slice(1).join('.');
  if (currentParent && currentParent === targetParent && currentParent.includes('.')) return false;

  return true;
}

/** The live session's token pair, or null when either half is missing (see the ⚠ in the header). */
export function hasHandoffTokens(): HandoffTokens | null {
  const { accessToken, refreshToken } = useSessionStore.getState();
  return accessToken && refreshToken ? { accessToken, refreshToken } : null;
}

/** `#zc_at=…&zc_rt=…`, url-encoded. Exported for tests; callers use `assignWithHandoff`. */
export function buildHandoffFragment(tokens: HandoffTokens): string {
  const params = new URLSearchParams();
  params.set(ACCESS_KEY, tokens.accessToken);
  params.set(REFRESH_KEY, tokens.refreshToken);
  return `#${params.toString()}`;
}

/** The inverse. Returns null unless BOTH tokens are present and non-empty. */
export function parseHandoffFragment(hash: string): HandoffTokens | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const accessToken = params.get(ACCESS_KEY);
  const refreshToken = params.get(REFRESH_KEY);
  return accessToken && refreshToken ? { accessToken, refreshToken } : null;
}

/**
 * THE ONE WAY THE LOGIN ORIGIN LEAVES FOR A PERSONA SPA. Every redirect out of the login app goes
 * through here — the sign-in form, the link-landing hook and all three persona pickers — so the
 * cross-site rule is decided once instead of at seven call sites that would drift apart.
 *
 * `url` must already have been produced or validated by the app registry (`appUrl` /
 * `isAllowedReturnUrl`); this function does not re-check the destination, it only decides whether to
 * carry the session to it. A malformed URL is assigned unchanged rather than throwing — losing the
 * redirect entirely would be worse than losing the handoff.
 */
export function assignWithHandoff(url: string, loc: HandoffLocation = window.location): void {
  let target: URL;
  try {
    target = new URL(url);
  } catch {
    window.location.assign(url);
    return;
  }

  const tokens = hasHandoffTokens();
  const carry =
    tokens !== null &&
    needsHandoff(loc.hostname, target.hostname) &&
    isSecureContextHost(loc.protocol, loc.hostname) &&
    isSecureContextHost(target.protocol, target.hostname);

  window.location.assign(carry ? `${target.toString()}${buildHandoffFragment(tokens)}` : target.toString());
}

/**
 * SPA side: take the token pair out of the URL and seed the session store with it, so the
 * `authApi.refresh()` the bootstrap already runs has a body token to send. Returns true when a
 * handoff was consumed.
 *
 * ⚠ THE FRAGMENT IS STRIPPED BEFORE ANYTHING ELSE HAPPENS, including before the tokens are stored —
 * `replaceState` rather than `pushState`, so Back does not return to a URL carrying a credential, and
 * synchronously, so no await can leave it sitting in the address bar. A browser without `history`
 * (or one that refuses the call) must not cost the user their login, so the failure is swallowed and
 * the handoff still proceeds.
 */
export function consumeAuthHandoff(loc: HandoffLocation = window.location): boolean {
  if (!loc.hash) return false;

  const tokens = parseHandoffFragment(loc.hash);
  if (!tokens) return false;

  // Refuse to read a credential off a plaintext URL, for the same reason we refuse to write one.
  if (!isSecureContextHost(loc.protocol, loc.hostname)) return false;

  try {
    globalThis.history?.replaceState(null, '', `${loc.pathname}${loc.search}`);
  } catch {
    /* no history API — the URL keeps the fragment; still better than failing the sign-in */
  }

  useSessionStore.getState().setTokens(tokens.accessToken, tokens.refreshToken);
  return true;
}
